import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Icon from './AppIcon';
import Button from './ui/Button';
import { sendNewsletterSubscriptionEmail } from '../utils/emailService';
import { scrollToElement } from '../utils/scrollUtils';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  // Handle smooth scrolling to sections
  const handleSectionClick = async (e, sectionId) => {
    e.preventDefault();
    
    // If we're on the homepage, just scroll to the section
    if (location.pathname === '/') {
      const success = await scrollToElement(sectionId, { 
        smooth: true,
        maxRetries: 15,
        retryDelay: 150
      });
      if (success) {
        // Update URL hash without triggering scroll
        window.history.pushState(null, '', `#${sectionId}`);
      }
    } else {
      // Navigate to homepage with hash
      navigate(`/#${sectionId}`);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const result = await sendNewsletterSubscriptionEmail(email);

      if (result.success) {
        setSubmitMessage('Thank you for subscribing to our newsletter!');
        setEmail('');
      } else {
        setSubmitMessage('Sorry, there was an error. Please try again.');
      }
    } catch (error) {
      setSubmitMessage('Sorry, there was an error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsAppClick = () => {
    const message = "Hi! I'm interested in your web development services. Can you help me get started?";
    const whatsappUrl = `https://wa.me/917574082582?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleEmailClick = () => {
    const subject = "Web Development Services Inquiry";
    const body = "Hi Octalchip team,\n\nI'm interested in your web development services. Could you please provide more information about your pricing and features?\n\nBest regards,\n[Your Name]";
    const mailtoUrl = `mailto:business@octalchip.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoUrl);
  };

  return (
    <footer className="bg-surface border-t border-border mt-16">
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6">
          {/* Company Info */}
          <div className="space-y-2 sm:space-y-3">
            <div className="flex items-center space-x-2">
              <img 
                src="/octalchiplogo.svg" 
                alt="OctalChip Logo" 
                className="h-6 sm:h-8 w-auto object-contain"
              />
            </div>
            <p className="text-xs sm:text-sm text-text-secondary leading-relaxed">
              Empowering businesses with cutting-edge web, mobile, AI, and cloud solutions for a digital future.
            </p>
            <div className="flex space-x-3 sm:space-x-4">
              <a 
                href="https://www.facebook.com/octalchip" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-text-secondary hover:text-primary transition-colors"
              >
                <Icon name="Facebook" size={20} />
              </a>
              <a 
                href="https://www.instagram.com/octalchiptech/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-text-secondary hover:text-primary transition-colors"
              >
                <Icon name="Instagram" size={20} />
              </a>
              <a 
                href="https://www.linkedin.com/company/octalchip" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-text-secondary hover:text-primary transition-colors"
              >
                <Icon name="Linkedin" size={20} />
              </a>
              <a 
                href="https://x.com/octalchip" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-text-secondary hover:text-primary transition-colors"
              >
                <Icon name="Twitter" size={20} />
              </a>
              <a 
                href="https://discord.gg/octalchip" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-text-secondary hover:text-primary transition-colors"
              >
                <Icon name="Discord" size={20} />
              </a>
            </div>
          </div>

          {/* Company Links */}
          <div className="space-y-2 sm:space-y-3">
            <h4 className="font-semibold text-text-primary text-base sm:text-lg">Company</h4>
            <ul className="space-y-1.5 sm:space-y-2">
              <li>
                <Link to="/about" className="text-xs sm:text-sm text-text-secondary hover:text-primary transition-colors flex items-center">
                  <Icon name="ChevronRight" size={12} className="mr-2" />
                  About
                </Link>
              </li>
              <li>
                <a 
                  href="/#contact" 
                  onClick={(e) => handleSectionClick(e, 'contact')}
                  className="text-xs sm:text-sm text-text-secondary hover:text-primary transition-colors flex items-center"
                >
                  <Icon name="ChevronRight" size={12} className="mr-2" />
                  Contact
                </a>
              </li>
              <li>
                <a 
                  href="/#services" 
                  onClick={(e) => handleSectionClick(e, 'services')}
                  className="text-xs sm:text-sm text-text-secondary hover:text-primary transition-colors flex items-center"
                >
                  <Icon name="ChevronRight" size={12} className="mr-2" />
                  Services
                </a>
              </li>
              <li>
                <a 
                  href="/#why-choose-us" 
                  onClick={(e) => handleSectionClick(e, 'why-choose-us')}
                  className="text-xs sm:text-sm text-text-secondary hover:text-primary transition-colors flex items-center"
                >
                  <Icon name="ChevronRight" size={12} className="mr-2" />
                  Why Choose Us
                </a>
              </li>
            </ul>
          </div>

          {/* Resources */}
          <div className="space-y-2 sm:space-y-3">
            <h4 className="font-semibold text-text-primary text-base sm:text-lg">Resources</h4>
            <ul className="space-y-1.5 sm:space-y-2">
              <li>
                <Link to="/blog" className="text-xs sm:text-sm text-text-secondary hover:text-primary transition-colors flex items-center">
                  <Icon name="ChevronRight" size={12} className="mr-2" />
                  Blog
                </Link>
              </li>
              <li>
                <a 
                  href="/#services" 
                  onClick={(e) => handleSectionClick(e, 'services')}
                  className="text-xs sm:text-sm text-text-secondary hover:text-primary transition-colors flex items-center"
                >
                  <Icon name="ChevronRight" size={12} className="mr-2" />
                  Our Services
                </a>
              </li>
              <li>
                <a 
                  href="/#skills" 
                  onClick={(e) => handleSectionClick(e, 'skills')}
                  className="text-xs sm:text-sm text-text-secondary hover:text-primary transition-colors flex items-center"
                >
                  <Icon name="ChevronRight" size={12} className="mr-2" />
                  Skills
                </a>
              </li>
              <li>
                <a 
                  href="/#industries" 
                  onClick={(e) => handleSectionClick(e, 'industries')}
                  className="text-xs sm:text-sm text-text-secondary hover:text-primary transition-colors flex items-center"
                >
                  <Icon name="ChevronRight" size={12} className="mr-2" />
                  Industries
                </a>
              </li>
              <li>
                <a 
                  href="/#contact" 
                  onClick={(e) => handleSectionClick(e, 'contact')}
                  className="text-xs sm:text-sm text-text-secondary hover:text-primary transition-colors flex items-center"
                >
                  <Icon name="ChevronRight" size={12} className="mr-2" />
                  Get Quote
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div className="space-y-2 sm:space-y-3">
            <h4 className="font-semibold text-text-primary text-base sm:text-lg">Legal</h4>
            <ul className="space-y-1.5 sm:space-y-2">
              <li>
                <Link to="/privacy" className="text-xs sm:text-sm text-text-secondary hover:text-primary transition-colors flex items-center">
                  <Icon name="ChevronRight" size={12} className="mr-2" />
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-xs sm:text-sm text-text-secondary hover:text-primary transition-colors flex items-center">
                  <Icon name="ChevronRight" size={12} className="mr-2" />
                  Terms of Service
                </Link>
              </li>
              <li>
                <a 
                  href="/#contact" 
                  onClick={(e) => handleSectionClick(e, 'contact')}
                  className="text-xs sm:text-sm text-text-secondary hover:text-primary transition-colors flex items-center"
                >
                  <Icon name="ChevronRight" size={12} className="mr-2" />
                  Support
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-2 sm:space-y-3">
            <h4 className="font-semibold text-text-primary text-base sm:text-lg">Contact</h4>
            <div className="space-y-1.5 sm:space-y-2">
              <div className="flex items-start space-x-2">
                <Icon name="MapPin" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                <div className="text-xs sm:text-sm text-text-secondary">
                  <p className="font-medium text-text-primary">Head Office</p>
                  <p>Octalchip, 404, Opera point,</p>
                  <p>kothariya, gujarat, india, 360022</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <Icon name="Mail" size={16} className="text-primary flex-shrink-0" />
                <a 
                  href="mailto:business@octalchip.com" 
                  className="text-xs sm:text-sm text-text-secondary hover:text-primary transition-colors break-all"
                >
                  business@octalchip.com
                </a>
              </div>
              
              <div className="flex items-center space-x-2">
                <Icon name="Phone" size={16} className="text-primary flex-shrink-0" />
                <a 
                  href="tel:+917574082582" 
                  className="text-xs sm:text-sm text-text-secondary hover:text-primary transition-colors"
                >
                  +91 7574082582
                </a>
              </div>
              
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={16} className="text-primary flex-shrink-0" />
                <span className="text-xs sm:text-sm text-text-secondary">Mon - Fri, 9AM - 6PM</span>
              </div>
            </div>
          </div>
        </div>

        {/* All Buttons and Newsletter in One Line */}
        <div className="mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-border">
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 items-stretch sm:items-center justify-center">
            <Button
              variant="outline"
              size="sm"
              iconName="MessageCircle"
              className="border-primary text-primary bg-white hover:bg-primary hover:text-white w-full sm:w-auto"
              onClick={handleWhatsAppClick}
            >
              WhatsApp Us
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              iconName="Mail"
              className="border-primary text-primary bg-white hover:bg-primary hover:text-white w-full sm:w-auto"
              onClick={handleEmailClick}
            >
              Email Us
            </Button>
            
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto sm:flex-1 sm:max-w-md">
              <input
                type="email"
                placeholder="Subscribe our newsletter to stay updated"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="px-3 py-2 border border-primary rounded text-xs sm:text-sm focus:outline-none focus:ring-1 focus:ring-primary/20 focus:border-primary flex-1"
                required
              />
              <Button
                type="submit"
                variant="outline"
                size="sm"
                className="border-primary text-primary bg-white hover:bg-primary hover:text-white w-full sm:w-auto"
              >
                Subscribe
              </Button>
            </form>
          </div>
          {submitMessage && (
            <p className="text-xs sm:text-sm text-center mt-2 text-text-secondary">
              {submitMessage}
            </p>
          )}
        </div>
      </div>

      {/* Copyright Bar */}
      <div className="bg-primary text-white py-3 sm:py-4">
        <div className="mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs sm:text-sm">
            Copyright © 2025 Octalchip | Powered by{' '}
            <a 
              href="https://www.octalchip.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:underline font-medium"
            >
              Octalchip
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 